import { useEffect, useRef } from 'react';
import { CircleAlert } from 'lucide-react';

import { Alert, AlertContent, AlertDescription, AlertTitle } from '@/components/Alert/Alert';
import { ApiError } from '@/lib/api';
import { cn } from '@/util/utils';

const COPY = {
    title: 'Could not save',
    body: 'The change did not reach the server. Nothing has been saved, and your edits are still here.',
} as const;

/**
 * Where a drawer's save failure lands: a reserved, aria-live slot below the form so nothing
 * jumps under the cursor when it appears. The API's own title and detail are shown for a
 * refusal it explained (a 4xx); anything else, a 5xx, a request that never reached the server
 * or an error that is not an ApiError at all, reads as "could not save". Pass null to show
 * nothing, for instance when the refusal is shown at the field it concerns.
 *
 * <p>Below a long form the slot can sit out of sight, and Save then looks as if it did nothing,
 * so each new failure is brought into view.</p>
 */
function SheetFailure({ failure, className }: { failure: ApiError | null; className?: string }) {
    const slot = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (failure) slot.current?.scrollIntoView({ block: 'nearest' });
    }, [failure]);
    const unexplained =
        failure !== null &&
        (!(failure instanceof ApiError) || failure.status >= 500 || failure.status === 0);
    return (
        <div
            ref={slot}
            aria-live="polite"
            className={cn('min-h-[5.25rem]', !failure && 'hidden sm:block', className)}
        >
            {failure ? (
                <Alert
                    variant="destructive"
                    className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-(--duration-base)"
                >
                    <CircleAlert />
                    <AlertContent>
                        <AlertTitle>{unexplained ? COPY.title : failure.problem.title}</AlertTitle>
                        <AlertDescription>
                            {unexplained ? COPY.body : failure.problem.detail}
                        </AlertDescription>
                    </AlertContent>
                </Alert>
            ) : null}
        </div>
    );
}

export { SheetFailure };
